import { z } from "zod";

/**
 * Begins a view's clock, at the world's start or a round boundary the spectator has passed. The
 * round that begins there takes its date from the label at `tick`.
 */
export const WorldOperationSchema = z.discriminatedUnion("type", [
  z.strictObject({
    type: z.literal("world.started"),
    roundCompleted: z.number().int().nonnegative(),
    tick: z.number().nonnegative(),
  }),
]);
export type WorldOperation = z.infer<typeof WorldOperationSchema>;

/** A perspective's word to the game master during a jump, apart from any input. */
export const InterventionOperationSchema = z.discriminatedUnion("type", [
  z.strictObject({
    type: z.literal("jump.intervened"),
    text: z.string().trim().min(1).max(20_000),
  }),
]);
export type InterventionOperation = z.infer<typeof InterventionOperationSchema>;

const JumpGuidanceSchema = z
  .string()
  .trim()
  .min(1)
  .max(500)
  .meta({ description: 'How far to go, such as "next major event", "1 week", or "1 year".' });

export const JumpOperationSchema = z.discriminatedUnion("type", [
  z.strictObject({
    type: z.literal("jump.started"),
    round: z.number().int().positive().meta({ description: "The round the jump ends." }),
    guidance: JumpGuidanceSchema.optional(),
  }),
  z.strictObject({
    type: z.literal("jump.completed"),
    roundCompleted: z.number().int().positive(),
    tick: z.number().nonnegative().meta({
      description:
        "Where the next round begins: where every timeline the jump reaches ends, each labelled there.",
    }),
  }),
]);
export type JumpOperation = z.infer<typeof JumpOperationSchema>;

/** A perspective's ask for the next jump, kept in its own view. */
export const JumpRequestOperationSchema = z.discriminatedUnion("type", [
  z.strictObject({ type: z.literal("jump.requested"), guidance: JumpGuidanceSchema.optional() }),
  z.strictObject({ type: z.literal("jump.requestWithdrawn") }),
]);
export type JumpRequestOperation = z.infer<typeof JumpRequestOperationSchema>;

/** The operations that move a view's clock. */
export type RoundOperation = WorldOperation | JumpOperation;

/** A perspective's ask for the next jump, and the perspective that asked. */
export interface JumpRequest {
  handle: string;
  guidance?: string;
}
